import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { MessageProvider } from './context/MessageContext';
import { AlertProvider } from './components/Alert';
import { ConfirmProvider } from './components/Confirm';
import { QuestionnairePromptProvider } from './components/QuestionnairePrompt';
import { initGA, logPageView, isGAEnabled, setUserProperties } from './utils/analytics';
import './config/amplify';

// Components
import Navigation from './components/Navigation';
import HelpButton from './components/HelpButton';
import PrivateRoute from './components/PrivateRoute';

// Pages
import SignUp from './pages/SignUp';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Settings from './pages/Settings';
import Questionnaire from './pages/Questionnaire';
import LeagueList from './pages/LeagueList';
import CreateLeague from './pages/CreateLeague';
import LeaguePage from './pages/LeaguePage';
import SuperLeaguePage from './pages/SuperLeaguePage';
import EditLeague from './pages/EditLeague';
import ManageMembers from './pages/ManageMembers';
import AdminPage from './pages/AdminPage';
import PlayerPage from './pages/PlayerPage';
import ClubPage from './pages/ClubPage';
import CreateClub from './pages/CreateClub';
import EditClub from './pages/EditClub';
import Landing from './pages/Landing';
import Support from './pages/Support';
import Terms from './pages/Terms';
import MessageCenter from './pages/MessageCenter';

import './App.css';

function AppRoutes() {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Set user properties in GA whenever user changes
  useEffect(() => {
    if (user && isGAEnabled()) {
      setUserProperties({
        user_name: user.name || '',
        user_email: user.email || ''
      });
    }
  }, [user]);

  // Track page views on route changes (only if GA is enabled)
  useEffect(() => {
    if (isGAEnabled()) {
      logPageView(location.pathname + location.search);
    }
  }, [location]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <>
      {user && <Navigation />}
      <Routes>
        {/* Public routes */}
        <Route path="/signup" element={!user ? <SignUp /> : <Navigate to="/questionnaire" />} />
        <Route path="/login" element={!user ? <Login /> : <Navigate to="/leagues" />} />
        <Route path="/forgot-password" element={!user ? <ForgotPassword /> : <Navigate to="/leagues" />} />
        <Route path="/terms" element={<Terms />} />

        {/* Protected routes */}
        <Route path="/questionnaire" element={<PrivateRoute><Questionnaire /></PrivateRoute>} />
        <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
        <Route path="/leagues" element={<PrivateRoute><LeagueList /></PrivateRoute>} />
        <Route path="/leagues/create" element={<PrivateRoute><CreateLeague /></PrivateRoute>} />
        <Route path="/leagues/:leagueId" element={<PrivateRoute><LeaguePage /></PrivateRoute>} />
        <Route path="/leagues/:leagueId/edit" element={<PrivateRoute><EditLeague /></PrivateRoute>} />
        <Route path="/leagues/:leagueId/members" element={<PrivateRoute><ManageMembers /></PrivateRoute>} />
        <Route path="/super-leagues/:leagueId" element={<PrivateRoute><SuperLeaguePage /></PrivateRoute>} />
        <Route path="/super-leagues/:leagueId/edit" element={<PrivateRoute><EditLeague /></PrivateRoute>} />
        <Route path="/super-leagues/:leagueId/members" element={<PrivateRoute><ManageMembers /></PrivateRoute>} />
        <Route path="/admin" element={<PrivateRoute><AdminPage /></PrivateRoute>} />
        <Route path="/players/:userId" element={<PrivateRoute><PlayerPage /></PrivateRoute>} />
        <Route path="/clubs/create" element={<PrivateRoute><CreateClub /></PrivateRoute>} />
        <Route path="/clubs/:clubId" element={<PrivateRoute><ClubPage /></PrivateRoute>} />
        <Route path="/clubs/:clubId/edit" element={<PrivateRoute><EditClub /></PrivateRoute>} />
        <Route path="/support" element={<PrivateRoute><Support /></PrivateRoute>} />
        <Route path="/messages" element={<PrivateRoute><MessageCenter /></PrivateRoute>} />

        {/* Public landing */}
        <Route path="/" element={!user ? <Landing /> : <Navigate to="/leagues" />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      {user && <HelpButton />}
    </>
  );
}

function App() {
  // Initialize Google Analytics on app load (only if GA is enabled)
  useEffect(() => {
    if (isGAEnabled()) {
      initGA();
    }
  }, []);

  return (
    <AuthProvider>
      <MessageProvider>
        <AlertProvider>
          <ConfirmProvider>
            <QuestionnairePromptProvider>
              <Router>
                <AppRoutes />
              </Router>
            </QuestionnairePromptProvider>
          </ConfirmProvider>
        </AlertProvider>
      </MessageProvider>
    </AuthProvider>
  );
}

export default App;

